/**
 * Shared rendering and export for AI artifacts (charts and tables). Used by the
 * chat (ArtifactRenderer) and the Documents page.
 */

import { useMemo } from "react";
import Chart from "components/charts/Chart";
import DataTable from "components/data-table";
import { EmptyState } from "components/states";
import { colorway, mergeOptions } from "lib/chartTheme";

const downloadBlob = (content, type, filename) => {
  const blob = new Blob([content], { type });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
};

/** Downloads a table artifact's data as CSV. */
export const exportTableCsv = ({ columns = [], rows = [], title }) => {
  const csvContent = [
    columns.join(","),
    ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
  ].join("\n");
  downloadBlob(csvContent, "text/csv;charset=utf-8;", `${title || "data"}.csv`);
};

/** Downloads a chart artifact's data as JSON. */
export const exportChartJson = (data) => {
  downloadBlob(JSON.stringify(data, null, 2), "application/json", `${data.title || "chart"}.json`);
};

/** Exports an artifact in its native format (CSV for tables, JSON for charts). */
export const exportArtifact = (type, data) => {
  if (!data) return;
  if (type === "table") exportTableCsv(data);
  else if (type === "chart") exportChartJson(data);
};

/**
 * Themed chart for a chart artifact (`data`: {type, options, series}). Series colors
 * from the artifact win; otherwise the chart palette is used.
 */
export const ArtifactChart = ({ id, data, height = 300 }) => {
  const { type = "bar", options = {}, series = [] } = data;

  const chartOptions = useMemo(
    () =>
      mergeOptions(
        {
          chart: {
            id: id || "ai-chart",
            toolbar: {
              show: true,
              tools: {
                download: true,
                selection: false,
                zoom: false,
                zoomin: false,
                zoomout: false,
                pan: false,
                reset: false,
              },
            },
          },
          colors: colorway,
        },
        options
      ),
    [id, options]
  );

  return <Chart type={type} series={series} options={chartOptions} height={height} />;
};

/** DataTable for a table artifact (`data`: {columns: string[], rows: any[][]}). */
export const ArtifactTable = ({ data, "aria-label": ariaLabel }) => {
  const { columns = [], rows = [] } = data;

  const tableColumns = useMemo(
    () =>
      columns.map((header, index) => ({
        id: String(index),
        header,
        accessorFn: (row) => row[index],
      })),
    [columns]
  );

  return (
    <DataTable
      aria-label={ariaLabel || data.title || "Table"}
      columns={tableColumns}
      data={rows}
      emptyState={<EmptyState variant="inline" title="No rows" />}
    />
  );
};
